// Top-level error boundary for the sales workspace.
//
// W22 (2026-10-20): the previous build mounted the full React tree
// behind HubSpotContextProvider + SessionProvider with no top-level
// boundary. If any provider threw synchronously during render, the
// whole tree bailed out and the iframe modal showed a blank page with
// no diagnostic text. This boundary catches those throws and renders
// the error + stack inline so future failures are visible to Doug
// without needing devtools.
//
// This complements, but does not replace, the per-screen
// CustomerReviewBoundary. That boundary scopes to a single screen; this
// one wraps the entire app so a throw in SessionProvider or
// HubSpotContextProvider still produces visible output.

import { Component, type ErrorInfo, type ReactNode } from 'react';

export interface RootErrorBoundaryProps {
  children: ReactNode;
}

interface RootErrorBoundaryState {
  error: Error | null;
}

export class RootErrorBoundary extends Component<
  RootErrorBoundaryProps,
  RootErrorBoundaryState
> {
  state: RootErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): RootErrorBoundaryState {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    // Log so the error is visible in devtools and remote console tools
    // (claude-in-chrome mcp__claude-in-chrome__read_console_messages).
    // Keep the log quiet in production builds that strip console.
    try {
      // eslint-disable-next-line no-console
      console.error('[sales-workspace] Root boundary caught:', error, info);
    } catch {
      /* ignore */
    }
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    const err = this.state.error;
    return (
      <main
        role="alert"
        style={{
          padding: 24,
          fontFamily:
            'system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
          maxWidth: 1100,
          margin: '0 auto',
        }}
      >
        <h1 style={{ marginTop: 0 }}>Sales Workspace could not start</h1>
        <p>
          Something threw while mounting the workspace. The screenshot
          below is the full error. Send this to Doug so the next build
          can patch the throw.
        </p>
        <pre
          style={{
            background: '#f5f5f5',
            border: '1px solid #ddd',
            padding: 16,
            borderRadius: 6,
            overflow: 'auto',
            fontSize: 12,
            lineHeight: 1.4,
          }}
        >
          {err.name}: {err.message}
          {'\n\n'}
          {err.stack ?? '(no stack available)'}
        </pre>
      </main>
    );
  }
}
